/**
 * AdminDashboard Component
 * 
 * Main admin dashboard for managing payments, refunds, and trademark applications
 * Styled to match the main dashboard with brand colors
 */
import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import {
  Settings,
  Users,
  CreditCard,
  FileText,
  BarChart3,
  Shield,
  AlertTriangle,
  ChevronRight,
  ArrowLeft,
  Lock,
  TrendingUp,
  Building2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../common';
import { H2, H3, Text } from '../../common/Typography';
import PaymentManagement from './PaymentManagement/PaymentManagement';
import TrademarkManagement from './TrademarkManagement/TrademarkManagement';
import UserManagement from './UserManagement/UserManagement';
import AdminStats from './AdminStats/AdminStats';
import Analytics from './Analytics/Analytics';
import LawFirmManagement from './LawFirmManagement/LawFirmManagement';
import markoAvatar from 'url:../../assets/marko-avatar.png';

const MARKOTM_ORG_ID = process.env.REACT_APP_MARKOTM_ORG_ID;

const AdminDashboard = () => {
  const { user, isLoaded } = useUser();
  const [activeSection, setActiveSection] = useState('stats');
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  // Check admin access only when component mounts (when /admin route is accessed)
  useEffect(() => {
    const checkAdminAccess = () => {
      if (user && user.organizationMemberships) {
        try {
          // Find MarkoTM organization by ID (from env var)
          const markoTMOrg = user.organizationMemberships.find(membership =>
            membership.organization.id === MARKOTM_ORG_ID
          );
          const isMarkoTMAdmin = markoTMOrg?.role === 'org:admin';
          
          console.log('🔍 AdminDashboard Access Check (on route access):', {
            isMarkoTMAdmin,
            markoTMFound: !!markoTMOrg,
            markoTMRole: markoTMOrg?.role,
            organizationCount: user.organizationMemberships?.length || 0,
            organizationNames: user.organizationMemberships?.map(m => m.organization.name) || []
          });
          
          // STRICT: Only allow admin access if user is admin in MarkoTM organization
          setHasAdminAccess(isMarkoTMAdmin);
        } catch (error) {
          console.error('Error checking admin access:', error);
          setHasAdminAccess(false);
        }
      } else {
        setHasAdminAccess(false);
      }
    };
    
    // Only check when user data is loaded and component mounts
    if (isLoaded) {
      checkAdminAccess();
    }
  }, []); // Empty dependency array - only run on mount when /admin route is accessed

  // If not loaded or no admin access, show appropriate message
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00796b] mx-auto mb-4"></div>
          <Text>Loading...</Text>
        </div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <H3 className="mb-2 text-red-900">Access Denied</H3>
            <Text className="text-gray-600 mb-4">
              You don't have permission to access the admin dashboard.
              Please contact your administrator if you believe this is an error.
            </Text>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-[#004d40] hover:bg-[#00695c] text-white px-4 py-2 rounded-md"
            >
              Return to Dashboard
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const navigationItems = [
    { 
      id: 'stats', 
      label: 'Overview', 
      icon: BarChart3,
      description: 'Dashboard statistics and metrics'
    },
    { 
      id: 'analytics', 
      label: 'Marketing Analytics', 
      icon: TrendingUp,
      description: 'Application funnel & conversion'
    },
    { 
      id: 'payments', 
      label: 'Payment Management', 
      icon: CreditCard,
      description: 'Manage payments and refunds'
    },
    { 
      id: 'trademarks', 
      label: 'Trademark Applications', 
      icon: FileText,
      description: 'Manage trademark applications'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: Users,
      description: 'Manage user accounts'
    },
    {
      id: 'lawfirms',
      label: 'Law Firms',
      icon: Building2,
      description: 'Manage law firm access'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'System configuration'
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'stats':
        return <AdminStats />;
      case 'analytics':
        return <Analytics />;
      case 'payments':
        return <PaymentManagement />;
      case 'trademarks':
        return <TrademarkManagement />;
      case 'users':
        return <UserManagement />;
      case 'lawfirms':
        return <LawFirmManagement />;
      case 'settings':
        return (
          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <Text>Settings panel coming soon...</Text>
            </CardContent>
          </Card>
        );
      default:
        return <AdminStats />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header - Matching main dashboard style */}
      <header className="bg-gradient-to-r from-[#004d40] via-[#00695c] to-[#00796b] shadow-md z-20 h-16 fixed top-0 w-full">
        <div className="px-4 h-full flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            {/* Back to Dashboard */}
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            
            <div className="h-6 w-px bg-white/20"></div>
            
            {/* Admin Logo */}
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-red-500/20 rounded-lg">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <span className="font-bold text-lg text-white">Admin</span>
                <span className="text-white/60 text-xs font-medium ml-1">Panel</span>
              </div>
            </div>
          </div>
          
          {/* Right section */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium text-white">
                {user?.firstName || 'Administrator'}
              </div>
              <div className="text-xs text-white/60">Admin Access</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-2 ring-white/30">
              {user?.imageUrl ? (
                <img src={user.imageUrl} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <Users className="h-4 w-4 text-white" />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="pt-16 flex min-h-screen">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white border-r border-gray-200 shadow-sm flex-shrink-0 fixed left-0 top-16 bottom-0 overflow-y-auto">
          <nav className="p-3 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              const isComingSoon = item.id === 'settings';
              
              return (
                <button
                  key={item.id}
                  onClick={() => !isComingSoon && setActiveSection(item.id)}
                  disabled={isComingSoon}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200
                    ${isActive 
                      ? 'bg-[#004d40] text-white shadow-md' 
                      : isComingSoon
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className={`
                    w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                    ${isActive 
                      ? 'bg-white/20' 
                      : isComingSoon
                        ? 'bg-gray-100'
                        : 'bg-[#e0f2f1]'
                    }
                  `}>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : isComingSoon ? 'text-gray-400' : 'text-[#00796b]'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>
                        {item.label}
                      </span>
                      {isComingSoon && (
                        <Lock className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                    {!isComingSoon && (
                      <p className={`text-xs truncate ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                        {item.description}
                      </p>
                    )}
                  </div>
                  
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-white/70" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <main className="flex-1 ml-64 p-6">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {navigationItems.find(item => item.id === activeSection)?.label || 'Overview'}
              </h1>
              <p className="text-gray-500 mt-1">
                {navigationItems.find(item => item.id === activeSection)?.description}
              </p>
            </div>
            
            {/* Content */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
